import React, { useMemo } from 'react';
import { useParams, Navigate, Outlet } from 'react-router';
import { observer } from 'mobx-react-lite';

import { SettingsPageLayout } from './settings-page-layout';
import { PageLoader } from '../../../app/components/page-loader';
import { useCurrentSpace } from '../../../hooks/use-current-space';

const baseMenu = [
  {
    key: 'general',
    label: 'General',
    href: `../settings/general`,
  },
  {
    key: 'billings',
    label: 'Billing',
    href: `../../settings/billings`,
  },
  {
    key: 'plans',
    label: 'Plans',
    href: `../settings/plans`,
  },
  {
    key: 'users',
    label: 'Users',
    href: `../settings/users`,
  },
  {
    key: 'tags',
    label: 'Tags',
    href: `../settings/tags`,
  },
  {
    key: 'storage',
    label: 'Storage',
    href: `../settings/storage`,
  },
];

export const SpaceSettingsPage: React.FC = observer(() => {
  const { spaceId } = useParams() as { spaceId: string };
  const space = useCurrentSpace();

  /**
   * Built per render rather than pushed into a module-level array, which added the extra entries
   * again on every visit.
   */
  const menu = useMemo(
    () => [
      ...baseMenu,
      ...(space?.flags?.isDeveloperAccessible
        ? [{ key: 'developers', label: 'Developers', href: `../settings/developers` }]
        : []),
      // Security holds the share link password policy, so every owner/admin needs it, not only
      // spaces with SSO. This whole page is already gated on `space.settings` below.
      { key: 'security', label: 'Security', href: `../settings/security` },
    ],
    [space?.flags?.isDeveloperAccessible],
  );

  if (!space || !space.flags) {
    return <PageLoader />;
  }

  if (!space.flags['space.settings']) {
    return <Navigate to={`/space/${spaceId}`} />;
  }

  return (
    <SettingsPageLayout title="Space settings" menu={menu}>
      <Outlet />
    </SettingsPageLayout>
  );
});
